import { inject } from '@angular/core';
import type { EnvironmentProviders } from '@angular/core';
import { KIT_FIREBASE_AUTH, kitGetIdToken } from '@rdlabo/ionic-angular-kit/auth-firebase';
import {
  KitMaintenanceController,
  KitOverlayController,
  KitReloadAlertController,
  provideKitHttp,
} from '@rdlabo/ionic-angular-kit';
import { environment } from './environments/environment';

const MUTATING_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

/**
 * odss 共通の HTTP 設定（mobile / customer 共用）。
 * 旧 InterceptorProvider を kitAuthInterceptor + provideKitHttp に置き換えたもの。
 * - Firebase ID トークンを `X-Amz-Security-Token` に付与。
 * - 通信エラー時はフリート共通の reload 確認を表示。
 * - 400 / 422 / 500 は API が返した message を表示する。
 * - 成功で残存アラートを dismiss。
 * - 204-as-error（旧 map の no-op 死にコード）は除去し、204 は正常 success として扱う。
 */
export const provideOdssHttp = (): EnvironmentProviders =>
  provideKitHttp(() => {
    const afAuth = inject(KIT_FIREBASE_AUTH);
    const reload = inject(KitReloadAlertController);
    const maintenance = inject(KitMaintenanceController);
    const overlay = inject(KitOverlayController);
    return {
      getAuthHeaders: async () => {
        const token = await kitGetIdToken(afAuth).catch((): null => null);
        return token ? { 'X-Amz-Security-Token': token } : {};
      },
      onResponse: () => void reload.dismiss(),
      offlineFallback: (request, error) => {
        if (MUTATING_METHODS.includes(request.method) && error.status === 0) {
          void reload.present({
            header: 'ネットワークエラー',
            message:
              '通信できませんでした。通信環境に問題のない場合、アプリをリフレッシュすることによって改善することがあります。リフレッシュしますか？（' +
              error.status +
              '）',
            okText: 'リフレッシュ',
          });
        }
        return null;
      },
      onServerError: (message) => {
        void overlay.alertClose({
          header: '実行できませんでした',
          message,
        });
      },
      onMaintenance: () =>
        void maintenance.present({
          header: 'メンテナンス中です',
          message: 'ただいまメンテナンスを行っています。終了次第、自動で閉じます。しばらくお待ちください。',
          waitUrl: environment.api() + 'public/maintenance/wait',
        }),
    };
  });
